import {Card, Form as AntForm} from 'antd';
import {Key, TablePaginationConfig, TableRowSelection} from 'antd/es/table/interface';
import classNames from 'classnames';
import React, {
    useEffect, useState, FC, Key as ReactKey,
} from 'react';
import {useDispatch} from 'react-redux';

import {ConfirmModal} from 'components/confirm-modal/confirm-modal';
import {Buttons} from 'components/form/buttons';
import {Spinner} from 'components/spinner';
import {
    getActionByType, loadMetadata, EMetaActionType, selectMetadata, ITableEntityMeta,
} from 'entities/metadata';
import {
    selectEntityData, TEntity, loadFormTableData,
} from 'modules/data';
import {performActionForFormTable, performActionForTableState, setData} from 'modules/data/data-actions';
import {
    IDefaultTableState,
    IDefaultTableStateFlat,
    IFilterEntityData,
    ITableEntityData,
} from 'modules/data/data-types';
import {IPageTemplateProps} from 'pages';
import {EEntityType} from 'shared/constants/entities';
import {ConfirmContent} from 'shared/constants/form';
import {ERequestType} from 'shared/types/requests';
import {useAppSelector} from 'store/config/hooks';
import {loadDocumentTypes} from 'store/slices/documents-uploading-slice';
import {loadRegions as loadRegionsAction} from 'store/slices/regions-slice';

import {Table} from '../table';
import {FormsFormTableContext} from './form-table-context';
import {convertObjectToTableStateFlat, normalizeFormTableData} from './form-table.utils';

import './form-table.less';

interface IFormTableComponentOwnProps
    extends Pick<IPageTemplateProps, 'entityName' | 'url' | 'hideTitle'> {
}

export interface IFormTableComponentProps extends IFormTableComponentOwnProps {
    entityName: string;
    entityType?: EEntityType;
}

export const FormTable: FC<IFormTableComponentProps> = ({
    entityName,
    entityType,
    url,
    hideTitle,
}: IFormTableComponentProps) => {
    const dispatch = useDispatch();
    const metadata = useAppSelector(selectMetadata(entityName, EEntityType.FORM_TABLE)) as ITableEntityMeta;
    const data = useAppSelector(selectEntityData(entityName, EEntityType.FORM_TABLE)) as ITableEntityData;
    const filter = useAppSelector(selectEntityData(entityName, EEntityType.FILTER)) as IFilterEntityData;

    const entityClassName = entityName?.split('.').join('-');
    const [isConfirmVisible, setConfirmVisible] = useState(false);
    const getConfirmAction = getActionByType(EMetaActionType.BUTTON_SAVE_FORM_PERSONAL_ACCOUNT)(metadata?.actions);
    const actionsClassNames = classNames(
        {
            'table-container__actions': true,
            [String(entityClassName)]: entityName,
        },
    );
    const fetchData = () => {
        const shouldLoadFilteredData = metadata?.isFilterable && filter?.data;
        const shouldLoadAllData = !metadata?.isFilterable;
        const tableDataOptions = {
            useContext: !!metadata?.useContext,
            useSearch: !!metadata?.isSearchable,
            disabledPagination: metadata?.disabledPagination,
            filterParamKey: metadata?.filterParamKey,
        };

        if (shouldLoadFilteredData) {
            dispatch(loadFormTableData(entityName, {...tableDataOptions, paramsToBeConverted: filter?.data}, url));
        } else if (shouldLoadAllData) {
            dispatch(loadFormTableData(entityName, {...tableDataOptions}, url));
        }
    };

    useEffect(() => {
        const shouldLoadMetadata = !metadata;
        const isEmptyData = !data?.rows;

        if (shouldLoadMetadata) {
            dispatch(loadMetadata(entityName, EEntityType.FORM_TABLE));
        }
        if (!shouldLoadMetadata && isEmptyData) {
            fetchData();
        }
    }, [data, entityName, metadata, filter]);

    useEffect(() => {
        if (metadata && metadata?.useContext) {
            dispatch(loadRegionsAction());
            dispatch(loadDocumentTypes());
        }
    }, [metadata?.useContext]);

    const setSelectedRows = (
        selectedRowKeys: ReactKey[],
        selectedRows: TEntity[],
    ) => dispatch(setData({
        entityName,
        entityType: EEntityType.FORM_TABLE,
        data: {

            selectedRowKeys,
            selectedRows,
        },
    }));
    const actionForFormTable = (
        referenceUrl: string,
        requestType: ERequestType,
        newData: Record<string, any>,
    ) => dispatch(performActionForFormTable(
        entityName, referenceUrl, requestType, newData,
    ));

    const changeTableState = (
        newData: IDefaultTableStateFlat,
    ) => dispatch(performActionForTableState({
        entityName,
        newEntityTypeData: newData as Record<string, any>,
        url,
    }));

    const rowSelection: TableRowSelection<Record<string, TEntity[]>> = {
        selectedRowKeys: data?.selectedRowKeys,
        onChange: setSelectedRows,
    };

    const isLoadedTable = metadata?.fields && data?.rows;
    const cardTitle = !hideTitle && metadata?.title;
    const isFormTable = true;
    const [form] = AntForm.useForm();

    const handleClose = () => {
        form.resetFields();
    };

    const handleOpenConfirmModal = () => {
        if (form.isFieldsTouched()) {
            setConfirmVisible(true);
        }
    };
    const handleFinish = (values: any) => {
        const saveAction = getActionByType(EMetaActionType.BUTTON_SAVE_FORM)(metadata.actions)
            || getActionByType(EMetaActionType.BUTTON_SAVE_FORM_PERSONAL_ACCOUNT)(metadata.actions);

        if (!saveAction) throw new Error('В метаданных не описано действие для сохранения');

        const isEmptyData = (value: any) => !value || !Object.keys(value).length;

        const {requestType, referenceUrl, additionalInfoKeys} = saveAction || {};

        const additionalValues: Record<string, any> = {};
        additionalInfoKeys?.forEach((key: string) => {
            additionalValues[key] = String(data.rows?.find(value => value[key])?.[key]);
        });

        if (requestType && referenceUrl && metadata.name && !isEmptyData(values)) {
            actionForFormTable(
                referenceUrl,
                requestType,
                normalizeFormTableData(values, additionalValues, metadata.linkField),
            );
        }
    };

    const handleFinishFailed = () => {
        console.error('Форма не прошла проверку');
    };

    const handleTableChange = (
        pagination: TablePaginationConfig,
        filters: Record<string, Key[] | null>,
        sorter: any,
    ) => {
        if (changeTableState) {
            changeTableState(
                convertObjectToTableStateFlat({pagination, sorter} as IDefaultTableState),
            );
        }
    };
    const handleFinishConfirm = () => {
        setConfirmVisible(false);
        handleFinish(form.getFieldsValue());
    };

    const handleCancelConfirm = () => {
        setConfirmVisible(false);
        form.resetFields();
    };

    const confirmModal = (
        <ConfirmModal
            title="Подтверждение"
            visible={isConfirmVisible}
            content={(getConfirmAction && ConfirmContent?.[getConfirmAction?.actionType])}
            onCancel={handleCancelConfirm}
            cancelText="Нет"
            onConfirm={handleFinishConfirm}
            approveText="Да"
            useWrapperDivElementAsContainer
        />
    );

    return (
        <Card
            title={cardTitle}
        >
            {confirmModal}
            <FormsFormTableContext.Provider
                value={{
                    form,
                }}
            >
                <AntForm
                    layout="vertical"
                    form={form}
                    onFinish={handleFinish}
                    onFinishFailed={handleFinishFailed}
                >
                    <div className={actionsClassNames}>
                        <Buttons
                            actions={metadata?.actions}
                            onClose={handleClose}
                            form={form}
                            handleOpenConfirmModal={handleOpenConfirmModal}
                        />
                    </div>
                    {isLoadedTable ? (
                        <Table
                            metadata={metadata as ITableEntityMeta}
                            isFormTable={isFormTable}
                            tableData={data}
                            url={url}
                            rowSelection={rowSelection}
                            onChange={handleTableChange}
                            entityName={entityName}
                            entityType={entityType}
                        />
                    ) : <Spinner />}
                </AntForm>
            </FormsFormTableContext.Provider>
        </Card>
    );
};
